import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Server, Database, Globe, Cpu, Network, HardDrive, Cloud } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Infrastructure & services cost dashboard (EUR). Reads the latest daily
// snapshot from /fleet/infra-cost-report (built by the "Pull Infrastructure
// Cost Snapshot" scheduled job -> cost_report.pull_cost_snapshot): real AWS
// spend via Cost Explorer + the manually-maintained external line items
// (infra_cost_items), all converted to EUR at the day's ECB rate. Admin-only.
// "Refresh now" rebuilds the snapshot so the numbers reflect freshly-edited
// external items without waiting for tomorrow's job.

const eur = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(n);

const GROUP_ICON = {
  "Server (EC2)": Server,
  "Database (RDS)": Database,
  "Public IP": Globe,
  "Data transfer": Network,
  "Storage (EBS)": HardDrive,
  "Other AWS": Cloud,
  AI: Cpu,
  "Mapping & Routing": Globe,
  Telematics: Network,
};

function Row({ name, group, eurVal, native, nativeCcy }) {
  const Icon = GROUP_ICON[group] || Cloud;
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800">
      <td className="py-2 pl-3 pr-2">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-slate-400 shrink-0" />
          <span className="font-medium">{name}</span>
        </div>
      </td>
      <td className="py-2 px-2 text-slate-500 text-xs">{group}</td>
      <td className="py-2 px-2 text-right tabular-nums text-slate-400 text-xs">
        {native != null ? `${native.toLocaleString()} ${nativeCcy}` : ""}
      </td>
      <td className="py-2 pr-3 pl-2 text-right tabular-nums font-semibold">{eur(eurVal)}</td>
    </tr>
  );
}

function Section({ title, subtotal, rows }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/60">
        <h3 className="font-semibold">{title}</h3>
        <span className="tabular-nums font-bold">{eur(subtotal)}</span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {rows.length === 0 ? (
            <tr><td className="py-3 px-4 text-slate-400 text-sm">No items.</td></tr>
          ) : (
            rows.map((r, i) => (
              <Row key={i} name={r.name} group={r.group} eurVal={r.eur} native={r.native} nativeCcy={r.native_ccy} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function InfraCostReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${apiBaseUrl}/fleet/infra-cost-report`, authHeaders())
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(e.response?.data?.detail || "Couldn't load the cost report."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    axios
      .post(`${apiBaseUrl}/fleet/infra-cost-report/refresh`, {}, authHeaders())
      .then(() => load())
      .catch((e) => setErr(e.response?.data?.detail || "Refresh failed."))
      .finally(() => setRefreshing(false));
  };

  if (loading) return <div className="p-6 text-slate-500">Loading cost report…</div>;
  if (err) return <div className="p-6 text-rose-600">{err}</div>;
  if (!data?.available)
    return (
      <div className="p-6">
        <p className="text-slate-500">{data?.message || "No cost snapshot yet."}</p>
        <button onClick={refresh} disabled={refreshing}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Build it now
        </button>
      </div>
    );

  const b = data.breakdown || {};
  const awsRows = b.aws || [];
  const extRows = b.external || [];

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Infrastructure &amp; Services Cost</h1>
          <p className="text-sm text-slate-500">
            {data.period_label} · all figures in EUR · 1 USD = {Number(data.usd_eur_rate).toFixed(4)} EUR
            {b.period_start ? ` · AWS billed ${b.period_start} → ${b.period_end}` : ""}
          </p>
        </div>
        <button onClick={refresh} disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm">
          <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border-2 border-slate-900 dark:border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Total / month</div>
          <div className="text-3xl font-bold tabular-nums mt-1">{eur(data.grand_total_eur)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">AWS</div>
          <div className="text-2xl font-semibold tabular-nums mt-1">{eur(data.aws_total_eur)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">External services</div>
          <div className="text-2xl font-semibold tabular-nums mt-1">{eur(data.external_total_eur)}</div>
        </div>
      </div>

      <Section title="AWS (actual billed)" subtotal={data.aws_total_eur} rows={awsRows} />
      <Section title="External services" subtotal={data.external_total_eur} rows={extRows} />

      <p className="text-xs text-slate-400">
        AWS figures come live from Cost Explorer (last complete month). External services are maintained by an admin
        in Tables → <span className="font-mono">infra_cost_items</span> — edit a monthly amount there and hit Refresh.
        Snapshot generated {data.generated_at?.slice(0, 16)} · updates daily at 06:00.
        {b.aws_mtd_eur != null ? ` · AWS month-to-date so far: ${eur(b.aws_mtd_eur)}.` : ""}
      </p>
    </div>
  );
}
